'use client';

import { getCookies } from 'cookies-next';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { childSearch, getRandomChildren } from '@sola/proxy-sdk/children.js';
import { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.js';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.js';
import { SAC_URL_PARAMS, SacFilterProps } from '@sola/schema/lib/schema.js';

const defaultLimit = 12;
const numberOfShowMoreRichMix = 10;

export const useSacChildren = (
  filters: SacFilterProps,
  allowShowMore: boolean,
) => {
  const {
    limit = defaultLimit,
    gender,
    ageMin,
    ageMax,
    countries,
    hiv,
    orphan,
    disability,
    highlyVulnerable,
  } = filters;

  const [children, setChildren] = useState<ChildAvailabilityItem[]>([]);
  const [richMixChildren, setRichMixChildren] = useState<
    ChildAvailabilityItem[] | undefined
  >();
  const params = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [viewMoreCount, setViewMoreCount] = useState(0);
  const [showRichMix, setShowRichMix] = useState(
    !(
      params?.get(SAC_URL_PARAMS.GENDER) ||
      params?.get(SAC_URL_PARAMS.AGE_MIN) ||
      params?.get(SAC_URL_PARAMS.AGE_MAX) ||
      params?.get(SAC_URL_PARAMS.COUNTRIES) ||
      params?.get(SAC_URL_PARAMS.BIRTH_DAY) ||
      params?.get(SAC_URL_PARAMS.BIRTH_MONTH) ||
      params?.get(SAC_URL_PARAMS.FILTERS) ||
      gender ||
      ageMin ||
      ageMax ||
      (countries && countries.length > 0) ||
      hiv ||
      disability ||
      orphan ||
      highlyVulnerable
    ),
  );

  const pathname = usePathname();
  const router = useRouter();
  const cookieStore = getCookies();

  const numberOfRichMixChildrenFetch = limit * numberOfShowMoreRichMix;

  const skip = viewMoreCount * limit;

  const resetFilters = () => router.push(pathname);

  const showNoMoreChildrenMessage =
    allowShowMore &&
    showRichMix &&
    viewMoreCount >= numberOfShowMoreRichMix - 1;

  const showLoadMoreButton =
    allowShowMore && viewMoreCount < numberOfShowMoreRichMix - 1;

  useEffect(() => {
    setIsLoading(true);
    if (params && Array.from(params.keys()).length > 0) {
      setShowRichMix(false);
    } else {
      setShowRichMix(true);
    }
    setViewMoreCount(0);
    setRichMixChildren([]);
    setChildren([]);
    setIsLoading(false);
  }, [params]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (showRichMix) {
        return;
      }
      if (!skip) {
        setIsLoading(true);
      }

      const results = await childSearch({
        take: limit,
        skip,
        gender:
          (params?.get(SAC_URL_PARAMS.GENDER) as ChildGenderEnum) ||
          (gender as ChildGenderEnum) ||
          '',
        ageMin:
          Number(params?.get(SAC_URL_PARAMS.AGE_MIN)) || ageMin || undefined,
        ageMax:
          Number(params?.get(SAC_URL_PARAMS.AGE_MAX)) || ageMax || undefined,
        countries: params?.get(SAC_URL_PARAMS.COUNTRIES)
          ? [params.get(SAC_URL_PARAMS.COUNTRIES) || '']
          : countries,
        // TODO: once we settle on the Contentful schema, we'll know more how to handle this
        birthDay: params?.get(SAC_URL_PARAMS.BIRTH_DAY),
        birthMonth: params?.get(SAC_URL_PARAMS.BIRTH_MONTH),
        isOrphan:
          params
            ?.get(SAC_URL_PARAMS.FILTERS)
            ?.includes(SAC_URL_PARAMS.ORPHANED_CHILD) ?? orphan,
        isInHivAffectedArea:
          params
            ?.get(SAC_URL_PARAMS.FILTERS)
            ?.includes(SAC_URL_PARAMS.AFFECTED_HIV) ?? hiv,
        disability:
          params
            ?.get(SAC_URL_PARAMS.FILTERS)
            ?.includes(SAC_URL_PARAMS.HAS_DISABILITIES) ?? disability,
        referrer: cookieStore.referrer,

        // Todo: Add highly vulnerable entry in the Api
        // isVulnerable:
        //   params
        //     ?.get(SAC_URL_PARAMS.FILTERS)
        // ?.includes(SAC_URL_PARAMS.VULNERABLE_EXPLOITATION) ?? highlyVulnerable,
        // todo: Vulnerable to Exploitation when supported by API
      });

      if (!cancelled) {
        if (skip) {
          setChildren((c) => [...c, ...(results?.children || [])]);
        } else {
          setChildren(() => [...(results?.children || [])]);
        }
      }
      if (!skip) {
        setIsLoading(false);
      }
    };

    fetchData();

    return (): void => {
      cancelled = true;
    };
  }, [
    limit,
    skip,
    params,
    gender,
    ageMax,
    ageMin,
    countries,
    hiv,
    disability,
    orphan,
    showRichMix,
  ]);

  useEffect(() => {
    if (!showRichMix) {
      return;
    }
    const fetchData = async () => {
      setIsLoading(true);
      const results = await getRandomChildren(numberOfRichMixChildrenFetch);
      setRichMixChildren(results?.children);
      setIsLoading(false);
    };

    fetchData();
  }, [numberOfRichMixChildrenFetch, showRichMix]);

  return {
    isLoading,
    children,
    richMixChildren,
    showRichMix,
    showNoMoreChildrenMessage,
    showLoadMoreButton,
    viewMoreCount,
    setViewMoreCount,
    resetFilters,
    limit,
  };
};
